import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import * as Api from "../lib/api";
import { Loader2, Mail, Trash2, Copy, UserPlus, Shield, Send } from "lucide-react";
import { toast } from "sonner";

export default function TeamPage() {
  const { user, team, refresh } = useAuth();
  const [data, setData] = useState({ members: [], pending_invites: [] });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await Api.listMembers());
    } catch (e) { /* network error — empty UI is acceptable */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function invite(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await Api.inviteTech(email.trim());
      toast.success("Invite created");
      setEmail("");
      navigator.clipboard?.writeText(r.invite_url).catch(() => {});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
    } finally { setBusy(false); }
  }
  async function revoke(id) {
    if (!window.confirm("Revoke this pending invite?")) return;
    await Api.revokeInvite(id); toast.success("Revoked"); load();
  }
  async function resend(id) {
    try {
      const r = await Api.resendInvite(id);
      if (r.email_sent) toast.success(`Invite re-sent to ${r.email}`);
      else toast.success("Invite re-sent (link copied)");
      navigator.clipboard?.writeText(r.invite_url).catch(() => {});
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) toast.error("This invite expired — revoke and create a new one.");
      else toast.error(e?.response?.data?.detail || "Could not resend invite");
    }
  }
  async function remove(id) {
    if (!window.confirm("Remove this technician? Their seat will be released and the next invoice prorated.")) return;
    await Api.removeMember(id); toast.success("Removed"); load(); refresh();
  }
  function copyUrl(url) { navigator.clipboard?.writeText(url); toast.success("Link copied"); }

  async function toggleAdminSeat() {
    try {
      await Api.updateMe({ uses_calculator: !user.uses_calculator });
      toast.success("Updated");
      refresh();
    } catch (e) {
      toast.error("Could not update");
    }
  }

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid-dark">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <div>
          <div className="inline-block bg-[#F26522] text-black px-3 py-1 font-mono text-[11px] font-black tracking-[0.2em] uppercase mb-3">Team</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">{team?.name || "Team"}</h1>
          <p className="text-white/60 text-sm mt-2">Manage technicians. Each accepted invite adds one seat to your subscription.</p>
        </div>

        {isAdmin && (
          <div className="bg-[#111111] border-2 border-white/15 p-5" data-testid="admin-seat-card">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-5 h-5 text-[#F26522]" />
              <div className="font-heading text-base font-black uppercase text-white">Admin Seat</div>
            </div>
            <button
              type="button"
              data-testid="btn-toggle-admin-seat"
              onClick={toggleAdminSeat}
              className="w-full text-left flex items-center justify-between gap-3 p-3 bg-black border-2 border-white/10 hover:border-white/30 transition-colors"
            >
              <div>
                <div className="text-white font-bold">I use the calculator</div>
                <div className="text-xs text-white/60 mt-1">If ON, you count as a billable seat. If OFF, admin-only and free.</div>
              </div>
              <div className={`w-14 h-8 border-2 shrink-0 flex items-center px-1 transition-colors ${
                user?.uses_calculator ? "bg-[#F26522] border-[#F26522] justify-end" : "bg-transparent border-white/30 justify-start"
              }`}>
                <div className={`w-5 h-5 ${user?.uses_calculator ? "bg-black" : "bg-white/60"}`} />
              </div>
            </button>
          </div>
        )}

        {isAdmin && (
          <div className="bg-[#111111] border-2 border-white/15 p-5">
            <div className="flex items-center gap-3 mb-4">
              <UserPlus className="w-5 h-5 text-[#F26522]" />
              <div className="font-heading text-base font-black uppercase text-white">Invite Technician</div>
            </div>
            <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder="tech@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="input-invite-email"
                className="flex-1 h-14 px-4 border-2 border-white/15 bg-black text-base font-mono text-white focus:border-[#F26522] focus:outline-none"
                required
              />
              <button type="submit" disabled={busy} data-testid="btn-invite"
                className="h-14 px-6 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-sm flex items-center justify-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Send Invite
              </button>
            </form>
          </div>
        )}

        {/* Members */}
        <div>
          <div className="font-heading text-lg font-black uppercase text-white mb-3">Active Members</div>
          {loading && <div className="text-white/60 font-mono text-sm">Loading…</div>}
          <div className="space-y-2">
            {data.members.filter(m => m.active !== false).map(m => (
              <div key={m.id} className="bg-[#111111] border-2 border-white/15 p-4 flex items-center justify-between gap-3" data-testid={`member-${m.id}`}>
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white truncate">{m.email}</div>
                  <div className="font-mono text-[10px] tracking-wide uppercase text-[#F26522] mt-1">
                    {m.role}{m.role === "admin" && m.uses_calculator ? " · uses calc" : ""}{m.role === "admin" && !m.uses_calculator ? " · free" : ""}
                  </div>
                </div>
                {isAdmin && m.role !== "admin" && (
                  <button onClick={() => remove(m.id)} data-testid={`btn-remove-${m.id}`}
                    className="w-10 h-10 border-2 border-white/15 hover:border-[#F26522] hover:text-[#F26522] text-white/60 flex items-center justify-center transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pending invites */}
        {data.pending_invites?.length > 0 && (
          <div>
            <div className="font-heading text-lg font-black uppercase text-white mb-3">Pending Invites</div>
            <div className="space-y-2">
              {data.pending_invites.map(inv => {
                const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
                return (
                  <div key={inv.id} className="bg-[#111111] border-2 border-[#F26522] p-4 space-y-3" data-testid={`invite-${inv.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-mono text-sm text-white">{inv.email}</div>
                      {isAdmin && (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => resend(inv.id)}
                            data-testid={`btn-resend-${inv.id}`}
                            className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-white/70 hover:text-[#F26522] transition-colors"
                          >
                            <Send className="w-3 h-3" /> Resend
                          </button>
                          <button
                            onClick={() => revoke(inv.id)}
                            data-testid={`btn-revoke-${inv.id}`}
                            className="font-mono text-[10px] uppercase tracking-wider text-[#F26522] hover:underline"
                          >
                            Revoke
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="bg-black border border-white/10 p-3">
                      <div className="font-mono text-[9px] tracking-widest uppercase text-white/40 mb-1">Share this link</div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 font-mono text-[11px] text-[#F26522] truncate">{url}</code>
                        <button onClick={() => copyUrl(url)} className="shrink-0 px-2 h-8 border border-white/20 hover:border-[#F26522] text-white/70 hover:text-[#F26522] flex items-center gap-1 text-xs">
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
